/**
 * The live spreadsheet grid of a space sheet, built on the shared sheet
 * model (`@amoreg/shared/spaceSheet`) in the document's Yjs state.
 *
 * - Only the rows and columns on screen render, so a sheet of thousands of
 *   cells scrolls as lightly as an empty one. Column letters and row numbers
 *   stay put while the cells scroll.
 * - Click selects, Shift extends, typing replaces the cell, F2 or a double
 *   click edits it; Enter, Tab and the arrows move (and commit an edit).
 *   Delete clears the selected block.
 * - Copy writes the selected block as TSV; paste writes a TSV block from the
 *   selected cell (as copied from Excel or Sheets).
 * - Everyone else's selected cell is outlined in their colour with their
 *   name, from awareness (`sheetSelection`, names stamped by the server).
 *
 * One input stays focused at the selected cell the whole time: typing goes
 * into it, so no keystroke is lost between selecting and editing.
 *
 * Formulas keep their text and show the value the file saved; nothing
 * recalculates here, and the footer says so.
 *
 * @module components/spaces/sheet/SheetGrid
 */
import {
  addSheet,
  cellAddress,
  cellDisplayText,
  cellFromInput,
  cellInputText,
  cellKey,
  columnLabel,
  ensureFirstSheet,
  readSheetMeta,
  renameSheet,
  setSheetCells,
  sheetCells,
  sheetOrder,
  type SheetCell,
  type SheetCellChange,
} from "@amoreg/shared/spaceSheet";
import { PlusIcon } from "lucide-react";
import {
  type ClipboardEvent,
  type KeyboardEvent,
  type PointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { Awareness } from "y-protocols/awareness";
import type * as Y from "yjs";

import { cn } from "../../../lib/utils";
import { toastManager } from "../../ui/toast";
import {
  HEADER_HEIGHT,
  ROW_HEADER_WIDTH,
  ROW_HEIGHT,
  SHEET_SELECTION_FIELD,
  type CellPosition,
  type MoveKey,
  type SheetCursor,
  type SheetSelection,
  cellAtPoint,
  clearChanges,
  columnOffsets,
  columnWidthPx,
  copySelection,
  gridSize,
  moveSelection,
  pasteChanges,
  pastedSelection,
  selectionBounds,
  sheetCursors,
  singleCell,
  visibleColumns,
  visibleRows,
  writeBatches,
} from "./sheetGrid.logic";

/** Re-renders on every change to the document, local or remote. */
function useDocVersion(doc: Y.Doc): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion((current) => current + 1);
    doc.on("update", bump);
    return () => doc.off("update", bump);
  }, [doc]);
  return version;
}

function useCursors(awareness: Awareness): ReadonlyArray<SheetCursor> {
  const [cursors, setCursors] = useState<ReadonlyArray<SheetCursor>>([]);
  useEffect(() => {
    const read = () => setCursors(sheetCursors(awareness.getStates(), awareness.clientID));
    read();
    awareness.on("change", read);
    return () => awareness.off("change", read);
  }, [awareness]);
  return cursors;
}

function useViewport(element: HTMLDivElement | null) {
  const [viewport, setViewport] = useState({ top: 0, left: 0, width: 960, height: 560 });
  useLayoutEffect(() => {
    if (element === null) return;
    const read = () =>
      setViewport({
        top: element.scrollTop,
        left: element.scrollLeft,
        width: element.clientWidth,
        height: element.clientHeight,
      });
    read();
    const observer = new ResizeObserver(read);
    observer.observe(element);
    element.addEventListener("scroll", read, { passive: true });
    return () => {
      observer.disconnect();
      element.removeEventListener("scroll", read);
    };
  }, [element]);
  return viewport;
}

const isNumeric = (cell: SheetCell | undefined) => typeof cell?.v === "number";

interface Editing {
  readonly row: number;
  readonly col: number;
  readonly text: string;
  /** The cell's text when editing began; unchanged means nothing to write. */
  readonly initial: string;
}

export function SheetGrid({
  doc,
  awareness,
  editable,
}: {
  readonly doc: Y.Doc;
  readonly awareness: Awareness;
  readonly editable: boolean;
}) {
  const version = useDocVersion(doc);
  const ids = useMemo(() => sheetOrder(doc), [doc, version]);
  const [chosenSheet, setChosenSheet] = useState<string | null>(null);
  const sheetId = chosenSheet !== null && ids.includes(chosenSheet) ? chosenSheet : (ids[0] ?? null);
  const meta = useMemo(() => (sheetId === null ? null : readSheetMeta(doc, sheetId)), [doc, sheetId, version]);
  const names = useMemo(
    () => ids.map((id) => ({ id, name: id === sheetId && meta ? meta.name : (readSheetMeta(doc, id)?.name ?? "Sheet") })),
    [doc, ids, sheetId, meta, version],
  );
  const cells = sheetId === null ? null : sheetCells(doc, sheetId);
  const read = useCallback((row: number, col: number) => cells?.get(cellKey(row, col)), [cells]);

  // An editable sheet document that has no sheet yet gets its first one.
  useEffect(() => {
    if (editable && ids.length === 0) ensureFirstSheet(doc);
  }, [doc, editable, ids.length]);

  const size = gridSize({
    extentRows: meta?.extentRows ?? 0,
    extentCols: meta?.extentCols ?? 0,
    rowCount: meta?.rowCount ?? 0,
    colCount: meta?.colCount ?? 0,
  });
  const widths = meta?.colWidths;
  const offsets = useMemo(
    () => columnOffsets(size.cols, (col) => columnWidthPx(widths?.[col])),
    [size.cols, widths],
  );
  const totalWidth = ROW_HEADER_WIDTH + offsets[size.cols]!;
  const totalHeight = HEADER_HEIGHT + size.rows * ROW_HEIGHT;

  const [scroller, setScroller] = useState<HTMLDivElement | null>(null);
  const viewport = useViewport(scroller);
  const rows = visibleRows(viewport.top, viewport.height - HEADER_HEIGHT, size.rows);
  const cols = visibleColumns(offsets, viewport.left, viewport.width - ROW_HEADER_WIDTH);

  const [selection, setSelection] = useState<SheetSelection>(singleCell({ row: 0, col: 0 }));
  const [editing, setEditing] = useState<Editing | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dragging = useRef(false);
  const focus = selection.focus;
  const bounds = selectionBounds(selection);

  // Others see where this browser is.
  useEffect(() => {
    if (sheetId === null) return;
    awareness.setLocalStateField(SHEET_SELECTION_FIELD, { sheetId, row: focus.row, col: focus.col });
  }, [awareness, sheetId, focus.row, focus.col]);
  useEffect(() => () => awareness.setLocalStateField(SHEET_SELECTION_FIELD, null), [awareness]);

  const cursors = useCursors(awareness);
  const here = cursors.filter((cursor) => cursor.sheetId === sheetId);

  const write = useCallback(
    (changes: ReadonlyArray<SheetCellChange>) => {
      if (!editable || sheetId === null || changes.length === 0) return;
      // One update each, under the server's update limit: a large paste or
      // clear must reach everyone, not only this screen.
      for (const batch of writeBatches(changes)) setSheetCells(doc, sheetId, batch);
    },
    [doc, editable, sheetId],
  );

  /** Keep the focused cell on screen. */
  const reveal = useCallback(
    (position: CellPosition) => {
      if (scroller === null) return;
      const top = HEADER_HEIGHT + position.row * ROW_HEIGHT;
      const left = ROW_HEADER_WIDTH + offsets[position.col]!;
      const right = ROW_HEADER_WIDTH + offsets[position.col + 1]!;
      if (top - HEADER_HEIGHT < scroller.scrollTop) scroller.scrollTop = top - HEADER_HEIGHT;
      else if (top + ROW_HEIGHT > scroller.scrollTop + scroller.clientHeight) {
        scroller.scrollTop = top + ROW_HEIGHT - scroller.clientHeight;
      }
      if (left - ROW_HEADER_WIDTH < scroller.scrollLeft) scroller.scrollLeft = left - ROW_HEADER_WIDTH;
      else if (right > scroller.scrollLeft + scroller.clientWidth) {
        scroller.scrollLeft = right - scroller.clientWidth;
      }
    },
    [offsets, scroller],
  );

  const select = useCallback(
    (next: SheetSelection) => {
      setSelection(next);
      reveal(next.focus);
    },
    [reveal],
  );

  const commit = useCallback(
    (current: Editing) => {
      const previous = read(current.row, current.col);
      const cell = cellFromInput(current.text, previous);
      const before = cellInputText(previous);
      // Only what this person typed is written: Enter or a blur on an
      // untouched editor must not put the old text back over a
      // collaborator's change made meanwhile.
      if (current.text !== current.initial && current.text !== before) {
        write([{ row: current.row, col: current.col, cell }]);
      }
      setEditing(null);
    },
    [read, write],
  );

  const startEditing = (text: string) => {
    if (!editable) return;
    setEditing({
      row: focus.row,
      col: focus.col,
      text,
      initial: cellInputText(read(focus.row, focus.col)),
    });
  };

  const move = (key: MoveKey, shift: boolean) =>
    select(moveSelection(selection, key, { shift, rows: size.rows, cols: size.cols }));

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (editing !== null) {
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        commit(editing);
        move(event.key, event.shiftKey);
      } else if (event.key === "Escape") {
        event.preventDefault();
        setEditing(null);
      } else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        commit(editing);
        move(event.key, false);
      }
      return;
    }
    switch (event.key) {
      case "ArrowUp":
      case "ArrowDown":
      case "ArrowLeft":
      case "ArrowRight":
      case "Tab":
      case "Enter":
        event.preventDefault();
        move(event.key, event.shiftKey);
        return;
      case "F2":
        event.preventDefault();
        startEditing(cellInputText(read(focus.row, focus.col)));
        return;
      case "Delete":
      case "Backspace":
        event.preventDefault();
        write(clearChanges(bounds, cells));
        return;
      case "Escape":
        select(singleCell(focus));
        return;
    }
  };

  const onInput = (text: string) => {
    if (editing !== null) setEditing({ ...editing, text });
    else if (editable && text.length > 0) startEditing(text);
  };

  const onCopy = (event: ClipboardEvent<HTMLInputElement>) => {
    if (editing !== null) return;
    event.preventDefault();
    const text = copySelection(bounds, read);
    if (text === null) {
      toastManager.add({ type: "info", title: "The selection is too large to copy" });
      return;
    }
    event.clipboardData.setData("text/plain", text);
  };

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    if (editing !== null || !editable) return;
    event.preventDefault();
    const changes = pasteChanges(event.clipboardData.getData("text/plain"), { row: bounds.top, col: bounds.left }, read);
    if (changes === null || changes.length === 0) return;
    write(changes);
    // A ragged block's widest row sets the right edge, not its last cell.
    setSelection(pastedSelection({ row: bounds.top, col: bounds.left }, changes));
  };

  const positionAt = (event: PointerEvent<HTMLDivElement>): CellPosition | null => {
    if (scroller === null) return null;
    const box = scroller.getBoundingClientRect();
    return cellAtPoint(
      {
        x: event.clientX - box.left,
        y: event.clientY - box.top,
        scrollLeft: scroller.scrollLeft,
        scrollTop: scroller.scrollTop,
      },
      offsets,
      size.rows,
    );
  };

  // While a cell is being edited, a press inside its input moves the caret or selects text.
  const inEditor = (target: EventTarget) => editing !== null && target === inputRef.current;

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (inEditor(event.target)) return;
    const position = positionAt(event);
    if (position === null || event.button !== 0) return;
    event.preventDefault();
    if (editing !== null) commit(editing);
    setSelection(event.shiftKey ? { anchor: selection.anchor, focus: position } : singleCell(position));
    dragging.current = true;
    inputRef.current?.focus({ preventScroll: true });
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragging.current || inEditor(event.target)) return;
    const position = positionAt(event);
    if (position !== null) setSelection((current) => ({ anchor: current.anchor, focus: position }));
  };
  const stopDragging = () => {
    dragging.current = false;
  };

  const box = (row: number, col: number, rowSpan = 1, colSpan = 1) => ({
    top: HEADER_HEIGHT + row * ROW_HEIGHT,
    left: ROW_HEADER_WIDTH + offsets[col]!,
    width: offsets[Math.min(col + colSpan, size.cols)]! - offsets[col]!,
    height: rowSpan * ROW_HEIGHT,
  });

  const focusBox = box(focus.row, focus.col);
  const rangeBox = box(bounds.top, bounds.left, bounds.bottom - bounds.top + 1, bounds.right - bounds.left + 1);
  const active = read(focus.row, focus.col);

  const bodyCells = [];
  for (let row = rows.first; row <= rows.last; row += 1) {
    for (let col = cols.first; col <= cols.last; col += 1) {
      const cell = read(row, col);
      const display = cellDisplayText(cell);
      bodyCells.push(
        <div
          key={`${row}:${col}`}
          className={cn(
            "absolute truncate border-e border-b border-border/60 px-2 text-[13px] leading-[27px] text-foreground",
            isNumeric(cell) && "text-end tabular-nums",
            cell?.f !== undefined && cell.v === null && "italic text-muted-foreground",
          )}
          style={box(row, col)}
          data-cell={cellAddress(row, col)}
        >
          {display}
        </div>,
      );
    }
  }

  const columnHeaders = [];
  for (let col = cols.first; col <= cols.last; col += 1) {
    const selected = col >= bounds.left && col <= bounds.right;
    columnHeaders.push(
      <div
        key={col}
        className={cn(
          "absolute top-0 flex h-full items-center justify-center border-e border-b border-border text-[11px] font-medium text-muted-foreground",
          selected && "bg-primary/10 text-foreground",
        )}
        style={{ left: ROW_HEADER_WIDTH + offsets[col]!, width: offsets[col + 1]! - offsets[col]! }}
      >
        {columnLabel(col)}
      </div>,
    );
  }
  const rowHeaders = [];
  for (let row = rows.first; row <= rows.last; row += 1) {
    const selected = row >= bounds.top && row <= bounds.bottom;
    rowHeaders.push(
      <div
        key={row}
        className={cn(
          "absolute left-0 flex w-full items-center justify-center border-e border-b border-border text-[11px] tabular-nums text-muted-foreground",
          selected && "bg-primary/10 text-foreground",
        )}
        style={{ top: row * ROW_HEIGHT, height: ROW_HEIGHT }}
      >
        {row + 1}
      </div>,
    );
  }

  const [renaming, setRenaming] = useState<{ readonly id: string; readonly name: string } | null>(null);
  // Another sheet opens at A1: the selection must not point past its columns.
  const showSheet = (id: string) => {
    setChosenSheet(id);
    setEditing(null);
    setSelection(singleCell({ row: 0, col: 0 }));
    // A1 is at the top left, and typing goes straight into the new sheet.
    scroller?.scrollTo({ top: 0, left: 0 });
    inputRef.current?.focus({ preventScroll: true });
  };
  const hasFormulas = meta?.hasFormulas ?? false;

  return (
    <div className="flex flex-col overflow-hidden rounded-lg border border-border bg-background" data-sheet-grid="">
      <div className="flex h-9 items-center gap-2 border-b border-border px-2 text-[13px]">
        <span
          className="w-16 shrink-0 rounded border border-border/70 px-1.5 py-0.5 text-center font-mono text-xs text-muted-foreground"
          data-sheet-address=""
        >
          {cellAddress(focus.row, focus.col)}
        </span>
        <span className="min-w-0 flex-1 truncate font-mono text-xs text-foreground" data-sheet-formula="">
          {editing !== null ? editing.text : cellInputText(active)}
        </span>
      </div>
      <div
        ref={setScroller}
        className="relative h-[min(64vh,640px)] overflow-auto overscroll-contain select-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={stopDragging}
        onPointerLeave={stopDragging}
        onDoubleClick={(event) => {
          if (!inEditor(event.target)) startEditing(cellInputText(read(focus.row, focus.col)));
        }}
        data-sheet-scroller=""
      >
        <div className="relative" style={{ width: totalWidth, height: totalHeight }}>
          {/* Column letters, pinned to the top. */}
          <div className="sticky top-0 z-20 bg-muted" style={{ height: HEADER_HEIGHT, width: totalWidth }}>
            {columnHeaders}
            <div
              className="sticky left-0 top-0 z-10 border-e border-b border-border bg-muted"
              style={{ width: ROW_HEADER_WIDTH, height: HEADER_HEIGHT }}
            />
          </div>
          {/* Row numbers, pinned to the left. */}
          <div
            className="sticky left-0 z-10 bg-muted"
            style={{ width: ROW_HEADER_WIDTH, height: size.rows * ROW_HEIGHT }}
          >
            {rowHeaders}
          </div>
          <div className="absolute inset-0" style={{ top: 0 }}>
            {bodyCells}
            {bounds.top !== bounds.bottom || bounds.left !== bounds.right ? (
              <div className="pointer-events-none absolute bg-primary/10 ring-1 ring-primary/50 ring-inset" style={rangeBox} />
            ) : null}
            {here.map((cursor) => {
              const at = box(cursor.row, cursor.col);
              return (
                <div
                  key={cursor.key}
                  className="pointer-events-none absolute z-[5]"
                  style={{ ...at, boxShadow: `inset 0 0 0 2px ${cursor.color}` }}
                  data-sheet-cursor={cursor.name}
                  data-sheet-cursor-cell={cellAddress(cursor.row, cursor.col)}
                >
                  <span
                    className="absolute -top-4 right-0 rounded-sm px-1 text-[10px] font-medium leading-4 whitespace-nowrap text-white"
                    style={{ backgroundColor: cursor.color }}
                  >
                    {cursor.name}
                  </span>
                </div>
              );
            })}
            <input
              ref={inputRef}
              value={editing?.text ?? ""}
              onChange={(event) => onInput(event.target.value)}
              onKeyDown={onKeyDown}
              onCopy={onCopy}
              onPaste={onPaste}
              onBlur={() => {
                if (editing !== null) commit(editing);
              }}
              aria-label={`Cell ${cellAddress(focus.row, focus.col)}`}
              readOnly={!editable}
              autoComplete="off"
              spellCheck={false}
              className={cn(
                "absolute z-[6] border-0 px-2 text-[13px] outline-none ring-2 ring-primary ring-inset",
                editing !== null
                  ? "bg-background text-foreground shadow-md"
                  : "cursor-cell bg-transparent text-transparent caret-transparent",
              )}
              style={focusBox}
              data-sheet-input=""
              data-editing={editing !== null ? "" : undefined}
            />
          </div>
        </div>
      </div>
      <div className="flex h-9 items-center gap-1 border-t border-border bg-muted/40 px-1.5">
        <div className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto" role="tablist" aria-label="Sheets">
          {names.map((sheet) => {
            const elsewhere = cursors.filter((cursor) => cursor.sheetId === sheet.id && sheet.id !== sheetId);
            return renaming?.id === sheet.id ? (
              <input
                key={sheet.id}
                autoFocus
                value={renaming.name}
                onChange={(event) => setRenaming({ id: sheet.id, name: event.target.value })}
                onBlur={() => {
                  renameSheet(doc, sheet.id, renaming.name);
                  setRenaming(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.currentTarget.blur();
                  if (event.key === "Escape") setRenaming(null);
                }}
                className="h-7 w-32 rounded-md border border-border bg-background px-2 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Sheet name"
              />
            ) : (
              <button
                key={sheet.id}
                type="button"
                role="tab"
                aria-selected={sheet.id === sheetId}
                onClick={() => showSheet(sheet.id)}
                onDoubleClick={() => editable && setRenaming({ id: sheet.id, name: sheet.name })}
                className={cn(
                  "flex h-7 shrink-0 cursor-pointer items-center gap-1.5 rounded-md px-2.5 text-xs outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                  sheet.id === sheetId
                    ? "bg-background font-medium text-foreground shadow-xs ring-1 ring-border"
                    : "text-muted-foreground hover:bg-background/60 hover:text-foreground",
                )}
                data-sheet-tab={sheet.name}
              >
                {sheet.name}
                {elsewhere.slice(0, 3).map((cursor) => (
                  <span
                    key={cursor.key}
                    title={cursor.name}
                    className="size-1.5 rounded-full"
                    style={{ backgroundColor: cursor.color }}
                  />
                ))}
              </button>
            );
          })}
          {editable ? (
            <button
              type="button"
              aria-label="Add a sheet"
              onClick={() => showSheet(addSheet(doc, `Sheet${ids.length + 1}`))}
              className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-background/60 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            >
              <PlusIcon className="size-3.5" />
            </button>
          ) : null}
        </div>
        {hasFormulas ? (
          <span className="shrink-0 px-2 text-[11px] text-muted-foreground" data-sheet-formula-note="">
            Formulas show the value saved in the file; they don't recalculate here.
          </span>
        ) : null}
      </div>
    </div>
  );
}
